import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sun, Moon, Heart } from 'lucide-react';
import { productPath } from '../utils/helpers';
import { ProductStamp } from './design-system';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tops de hombre curados por el cliente (TOP_Ventas_Hombres_y_Mujeres.xlsx),
// en orden de prioridad: Jóvenes 1-10 seguido de Maduros 1-10.
const MALE_TOP_CODES = [
  'X047', 'X034', 'X058', 'X066', 'X029', 'X040', 'X105', 'X119', 'X207', 'X211',
  'X233', 'X276', 'X001', 'X008', 'X012', 'X054', 'X082', 'X101', 'X225', 'X251'
];

/**
 * MoodProductsSection - Sección de productos por mood/ocasión (exclusivamente Hombre)
 */
const MoodProductsSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  const tabs = [
    { 
      id: 'daily', 
      label: 'Uso Diario', 
      mood: 'Para el día a día',
      icon: Sun,
      color: '#fbbf24'
    },
    { 
      id: 'night', 
      label: 'Nocturno', 
      mood: 'Perfecto para la noche',
      icon: Moon,
      color: '#8b5cf6'
    },
    {
      id: 'sexy', 
      label: 'Sexy', 
      mood: 'Sexy',
      icon: Heart,
      color: '#ec4899'
    },
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    const productsByMood = {};
    
    for (const tab of tabs) {
      try {
        const response = await fetch(
          `${API_URL}/api/products?gender=Hombre&mood=${encodeURIComponent(tab.mood)}&limit=100`
        );
        let fetched = response.ok ? ((await response.json()).products || []) : [];

        // Si no hay perfumes de Hombre para este mood (ej. "Sexy"), usar Mujer como respaldo
        // en vez de dejar la sección vacía.
        if (fetched.length === 0) {
          const fallbackResponse = await fetch(
            `${API_URL}/api/products?gender=Mujer&mood=${encodeURIComponent(tab.mood)}&limit=100`
          );
          if (fallbackResponse.ok) {
            fetched = (await fallbackResponse.json()).products || [];
          }
        }

        // Prioriza los tops curados por el cliente para este mood, en su orden.
        const curated = MALE_TOP_CODES
          .map(code => fetched.find(p => p.product_code === code))
          .filter(Boolean);

        // Si hay menos de 4 tops curados, completar con el resto del mood en orden aleatorio.
        const rest = fetched
          .filter(p => !curated.includes(p))
          .sort(() => Math.random() - 0.5);
        productsByMood[tab.id] = [...curated, ...rest].slice(0, 4);
      } catch (error) {
        console.error(`Error loading products for ${tab.mood}:`, error);
        productsByMood[tab.id] = [];
      }
    }
    
    setProducts(productsByMood);
    setLoading(false);
  };

  const currentTab = tabs[activeTab];
  const currentProducts = products[currentTab?.id] || [];

  return (
    <section className="py-4 md:py-6 bg-brand-black">
      <div className="max-w-7xl mx-auto px-4">
        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-white/5 rounded-full p-1 gap-1">
            {tabs.map((tab, index) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === index;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(index)}
                  className={`
                    relative px-4 md:px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300
                    flex items-center gap-2
                    ${isActive 
                      ? 'text-white' 
                      : 'text-white/50 hover:text-white/70'
                    }
                  `}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 rounded-full"
                      style={{ backgroundColor: tab.color }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    <IconComponent size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Products Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 min-h-[320px] md:min-h-[420px]"
          >
            {loading ? (
              // Loading skeleton
              [...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-white/5 rounded-xl mb-3" />
                  <div className="h-4 bg-white/5 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-white/5 rounded w-1/2" />
                </div>
              ))
            ) : currentProducts.length > 0 ? (
              currentProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={productPath(product)}
                    className="group block"
                  >
                    {/* Product Image - Large */}
                    <div className={`relative aspect-square bg-gradient-to-b from-white/5 to-transparent rounded-xl overflow-hidden mb-4 border transition-all duration-300 ${
                      product.gender === 'Mujer'
                        ? 'border-transparent group-hover:border-brand-magenta group-hover:shadow-brand-magenta'
                        : 'border-transparent group-hover:border-brand-cobalt group-hover:shadow-brand-cobalt'
                    }`}>
                      <img
                        src={product.main_image || '/placeholder-perfume.png'}
                        alt={product.name}
                        className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
                      />
                      <ProductStamp />

                      {/* Mood Badge */}
                      <div 
                        className="absolute top-3 left-3 px-2 py-1 rounded-full text-[10px] font-medium text-white"
                        style={{ backgroundColor: currentTab.color }}
                      >
                        {currentTab.label}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="text-center">
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">
                        Inspirado en:
                      </p>
                      <h3 className={`text-white font-medium text-sm md:text-base mb-1 transition-all duration-300 line-clamp-2 ${
                        product.gender === 'Mujer'
                          ? 'group-hover:[text-shadow:0_0_10px_rgba(194,24,91,0.8)]'
                          : 'group-hover:[text-shadow:0_0_10px_rgba(27,79,216,0.8)]'
                      }`}>
                        {product.inspired_by_name || product.name}
                      </h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">
                        {product.inspired_by_brand || product.brand_name}
                      </p>
                      <p className="text-white/80 font-semibold">
                        ${product.sizes?.[0]?.price || product.base_price || 499}
                        <span className="text-white/40 text-xs ml-1">MXN</span>
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              // No products message
              <div className="col-span-2 md:col-span-4 flex items-center justify-center text-center">
                <p className="text-white/40">No hay productos disponibles para esta categoría</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* View All Button - reserva su espacio aunque no haya productos, para que la sección no cambie de alto */}
        <div className={`text-center mt-10 ${currentProducts.length > 0 ? '' : 'invisible'}`}>
          <Link
            to={`/products?mood=${encodeURIComponent(currentTab.mood)}`}
            className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 rounded-full text-white text-sm hover:bg-white/10 transition-colors"
          >
            Ver todos los perfumes {currentTab.label.toLowerCase()}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MoodProductsSection;
